<template>
    <div class="c-blog__listing" ref="sectionRef">
        <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/choices.js/public/assets/styles/choices.min.css" />
        <Filters
            :categoriesFilters="categoriesFilters"
            :mrcFilters="mrcFilters"
            :activeFilters="activeFilters"
            @filtersSubmitted="filtersSubmitHandler"
        />

        <template v-if="!currentArticles || currentArticles.length == 0">
            <p class="c-blog__listing__empty h3">{{ $t('blog.default') }}</p>
        </template>

        <template v-else>
            <div class="c-blog__gallery u-withLoader" :class="{'is-loading': isLoading }">
                <div class="u-withLoader--blurred">
                    <ul class="unstyled" v-if="currentArticles">
                        <Card
                            v-for="article of currentArticles" :key="article.id"
                            :article="article"
                            :categories="categoriesFilters"
                            :archiveUrl="archiveUrl"
                        />
                    </ul>
        
                    <Loader />
                </div>
            </div>
            
            <Pagination
                v-if="totalPages > 1"
                @increment="increment" 
                @decrement="decrement" 
                :currentPage="currentPage" 
                :totalPages="totalPages"
            />
        </template>
    </div>
</template>

<script>
    import axios from 'axios';

    import Card from './components/Card.vue';
    import Filters from './components/Filters.vue';
    import Loader from './components/Loader.vue';
    import Pagination from './components/Pagination.vue';

    import { nextTick, toRaw } from 'vue';

    export default {
        name: 'BlogListing',
        components: {
            Card,
            Filters,
            Loader,
            Pagination,
        },

        data() {
            return {
                lang: window.Store.app.lang,
                config: {
                    api: '/wp-json/acolyte/v1/get-articles',
                    lang: window.Store.app.lang
                },
                activeFilters: {
                    categories: [],
                    mrcs: [],
                },
                apiParams: { },
                currentArticles: null,
                currentPage: 1,
                totalPages: 1,
                isLoading: false,
            }
        },
        props: {
            categoriesFilters: {
                type: Object,
                default: null
            },
            mrcFilters: {
                type: Array,
                default: null
            },
            archiveUrl: {
                type: String,
                default: ''
            },
        },
        mounted() {
            this.setFilters();

            window.addEventListener('popstate', (e) => {
                this.currentPage = e.state.currentPage;
                this.totalPages = e.state.totalPages,
                this.apiParams = e.state.apiParams;

                this.getArticles(false);
            });
        },
        computed: { },
        methods: {
            setFilters(){
                // Set filters on-load
                if(this.activeFilters){
                    if(this.activeFilters?.categories){
                        this.apiParams.category = this.activeFilters.categories;
                    }

                    if(this.activeFilters?.mrcs){
                        this.apiParams.mrc = this.activeFilters.mrcs;
                    }
                }

                this.checkUrlPageNumber();
                this.checkUrlParam();

                this.getArticles(false);
            },
            /**
            * Call the API for Blog articles to display in listing
            */
            getArticles(updateUrl = true) {
                /* show loading */
                this.isLoading = true;

                axios.get(this.config.api, {
                    params: {
                        lang: this.config.lang,
                        ...this.apiParams
                    }
                })
                .then((response) => {
                    this.isLoading = false;

                    this.currentArticles = response.data.articles;
                    this.currentPage = response.data.current_page;
                    this.totalPages = response.data.total_pages;

                    window.Store.scrollbar.update();
                    
                    if(updateUrl){
                        this.updateUrl();
                    }
                }).catch((err) => {
                    console.warn(err);
                })
            },
            /**
            * Update the apiParams with the new filters values
            */
            filtersSubmitHandler(filters){
                this.apiParams.page = 1;
                
                if(filters.category){
                    this.apiParams.category = filters.category;
                }

                if(filters.mrc){
                    this.apiParams.mrc = filters.mrc;
                }

                if(filters.article !== ''){
                    this.apiParams.article = filters.article;
                }
                else{
                    delete this.apiParams.article
                }

                this.getArticles();
            },
            updateLoadingState(state){
                this.isLoading = state;
            },
            increment(page) {
                if (page) {
                    this.currentPage = page;
                } else {
                    this.currentPage++;
                }

                this.apiParams.page = this.currentPage;

                this.getArticles();
                window.Store.scrollbar.scrollTo(this.$refs.sectionRef)
            },
            decrement(page) {
                if (page) {
                    this.currentPage = page;
                } else {
                    this.currentPage--;
                }

                this.apiParams.page = this.currentPage;

                this.getArticles();
                window.Store.scrollbar.scrollTo(this.$refs.sectionRef)
            },
            /**
            *   Update the pagination state of activeFilters and ApiParams if there are values in URL
            */
            checkUrlPageNumber() {
                const url = new URL(window.location.href);
                const pathname = url.pathname;
                const match = pathname.match(/\/page\/(\d*)/);

                if (match) {
                    this.currentPage = parseInt(match[1]);
                    this.apiParams.page = parseInt(match[1]);
                }
            },
            /**
            *   Update the activeFilters and ApiParams if there are values in URL
            */
            checkUrlParam() {
                const url = new URL(window.location.href);

                for(const [key, value] of url.searchParams.entries()){
                    if(key === 'article'){
                        this.activeFilters.article = value;
                        this.apiParams.article = value;
                    }
                    else if(key.search(/cat\[\d+\]/g) > -1 || key.search(/cat\[\]/g) > -1){
                        if(this.activeFilters['categories']){
                            this.activeFilters['categories'].push(value);
                        }
                        else{
                            this.activeFilters['categories'] = [value];
                        }
                    }
                    else if(key.search(/mrc\[\d+\]/g) > -1 || key.search(/mrc\[\]/g) > -1){
                        if(this.activeFilters['mrcs']){
                            this.activeFilters['mrcs'].push(value);
                        }
                        else{
                            this.activeFilters['mrcs'] = [value];
                        }
                    }
                    else{
                        if(key === 'page' || !this.apiParams[key]?.length){
                            continue;
                        }

                        this.activeFilters[key] = value;
                        this.apiParams[key] = value;
                    }
                }
            },
            /** 
            *   Update the Url with activeFilters and pagination values
            */
            updateUrl() {
                const url = new URL(window.location.href);
                url.searchParams.delete('cat[]');
                url.searchParams.delete(/cat\[\d+\]/g);
                url.searchParams.delete('mrc[]');
                url.searchParams.delete(/mrc\[\d+\]/g);
                url.searchParams.delete('article');

                // Pagination handling
                const baseUrl = url.href.split('/page/')[0];

                if (this.totalPages > 1 && this.currentPage > 1) {
                    url.href = `${baseUrl}/page/${this.currentPage}`;
                } else {
                    url.href = `${baseUrl}`;
                }

                // Filters handling
                for(const paramName in this.apiParams){
                    if(paramName === 'category'){
                        if(this.apiParams[paramName].length){
                            for(const value of this.apiParams[paramName]){
                                url.searchParams.append(`cat[]`, value);
                            }
                        }
                    }
                    else if(paramName === 'mrc'){
                        if(this.apiParams[paramName].length){
                            for(const value of this.apiParams[paramName]){
                                url.searchParams.append(`mrc[]`, value);
                            }
                        }
                    }
                    else{
                        if(this.apiParams[paramName]){
                            if(paramName === 'page' || !this.apiParams[paramName].length){
                                continue;
                            }

                            url.searchParams.append(paramName, this.apiParams[paramName]);
                        }
                    }
                }

                history.pushState({
                    currentPage: this.currentPage,
                    totalPages: this.totalPages,
                    apiParams: toRaw(this.apiParams),
                }, '', url);
            },
        }
    }
</script>